import { Outlet } from "react-router-dom";
import useHomepage from "pages/Home/hooks/useHomepage";
import { PageHeading } from "components/shared/PageHeading";
import ArrowRight from "assets/icons/arrowhead right.svg?react";
import LoadingScreen from "components/shared/LoadingScreen";
import { useSelector } from "react-redux";
import { RootState } from "redux/store/store";
import { translations, t } from "utils/i18n";

const NoGatewayPanel = ({ givenName }: { givenName: string }) => (
  <main className="h-full flex-container">
    <div className="relative flex h-full max-width">
      <div className="w-3/4 pr-3" id="PrimaryLeftContent">
        <section className="h-full">
          <div className="flex h-[7%] items-center justify-between space-x-3 pb-5 mt-8">
            <PageHeading>
              <h1>{translations.selectedGatewayLayout.greeting(givenName)}</h1>
            </PageHeading>
          </div>
          <div className="flex flex-col items-center justify-center h-[80%] text-center space-y-4">
            <svg
              className="w-20 h-20 text-deep-blue"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M12 5a7 7 0 100 14 7 7 0 000-14z"
              />
            </svg>
            <h2 className="text-xl font-semibold">
              {t("selectedGatewayLayout.noDevicesHeading")}
            </h2>
            <p className="text-gray-600">
              {t("selectedGatewayLayout.noDevicesBody")}
            </p>
            <div className="space-y-4">
              <div className="flex justify-center items-center gap-8">
                <img
                  src="/QR.png"
                  alt={t("selectedGatewayLayout.backgroundAlt")}
                  className="w-50 h-30"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
      <span className="border border-deep-blue/20" />
      <div className="relative" />
      <div className="pb-4 rounded divide-elevation-2 bg-elevation-1 h-[25%] w-[25%] m-6">
        <section className="flex items-center justify-between px-3 py-4 text-center border-b-[1px] border-elevation-3">
          <div className="flex items-center space-x-2 ">
            <h1 className="font-bold">{t("selectedGatewayLayout.knowMore")}</h1>
          </div>
        </section>
        <section className="py-4 space-y-2">
          <div className="flex items-center mx-4 whitespace-nowrap overflow-hidden">
            <span className="text-interactive-blue truncate">
              {t("selectedGatewayLayout.aboutInhab")}
            </span>
            <ArrowRight className="ml-2 flex-shrink-0" />
          </div>
          <div className="flex items-center mx-4 whitespace-nowrap overflow-hidden">
            <span className="text-interactive-blue truncate">
              {t("selectedGatewayLayout.otherProducts")}
            </span>
            <ArrowRight className="ml-2 flex-shrink-0" />
          </div>
          <div className="flex items-center mx-4 whitespace-nowrap overflow-hidden">
            <span className="text-interactive-blue truncate">
              {t("selectedGatewayLayout.faqs")}
            </span>
            <ArrowRight className="ml-2 flex-shrink-0" />
          </div>
        </section>
      </div>
    </div>
  </main>
);

const SelectedGatewayLayout = () => {
  const { selectedGateway, isLoading, isError } = useHomepage();
  // Read Entra ID claims from Redux (set once on login in index.tsx)
  const userClaims = useSelector((state: RootState) => state.auth.claims);
  const givenName = (userClaims?.given_name ?? "") as string;

  // ⬅️ CASE 1: still fetching data
  if (isLoading) {
    return (
      <main className="h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <svg
            className="w-12 h-12 animate-spin text-deep-blue"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
          <LoadingScreen>{t("suspense.loading")}</LoadingScreen>
        </div>
      </main>
    );
  }

  // ⬅️ CASE 2: API error
  if (isError) {
    return <NoGatewayPanel givenName={givenName} />;
  }

  // ⬅️ CASE 3: finished fetching, no gateway
  if (!selectedGateway) {
    return <NoGatewayPanel givenName={givenName} />;
  }

  // ⬅️ CASE 4: gateway available
  return <Outlet />;
};

export default SelectedGatewayLayout;
